<script setup>
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

// Addendum 09, Modul AI — halaman antrian broadcast. POIN KRITIS: setiap
// baris punya tombolnya SENDIRI ("Kirim ke [Nama]") -- TIDAK ADA tombol
// "kirim semua sekaligus", supaya tidak memberi ilusi otomatisasi yang
// tidak ada (§4 addendum). Klik tombol membuka wa.me di tab baru DAN
// menandai status "dibuka" -- BUKAN "terkirim", karena sistem tidak bisa
// konfirmasi pesan benar-benar sampai di WhatsApp (itu terjadi di luar
// sistem, di app/web WhatsApp milik pengguna sendiri).

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Broadcast',
  },
})

const route = useRoute()
const { can } = useAbility()

const loading = ref(true)
const loadError = ref('')
const campaign = ref(null)
const openingId = ref(null)

const statusMeta = status => ({
  ready: { label: 'Siap Dikirim', color: 'info' },
  completed: { label: 'Selesai', color: 'success' },
  draft: { label: 'Draft', color: 'default' },
}[status] ?? { label: status, color: 'default' })

const fetchCampaign = async () => {
  loading.value = true
  loadError.value = ''
  try {
    const response = await useApi(`/api/v1/broadcast-campaigns/${route.params.id}`).get()
    const body = response?.data ?? response

    campaign.value = body?.data ?? body
  } catch (err) {
    loadError.value = 'Gagal memuat campaign.'
    console.error('[BroadcastCampaignQueue] fetch error', err)
  } finally {
    loading.value = false
  }
}

onMounted(fetchCampaign)

const openedCount = computed(() => campaign.value?.recipients?.filter(r => r.status === 'opened').length ?? 0)
const totalCount = computed(() => campaign.value?.recipients?.length ?? 0)
const progressPercent = computed(() => (totalCount.value ? (openedCount.value / totalCount.value) * 100 : 0))

// Sengaja SATU recipient per klik -- tidak ada loop/"kirim semua".
const sendTo = async recipient => {
  window.open(recipient.wa_link, '_blank', 'noopener')

  openingId.value = recipient.id
  try {
    const response = await useApi(
      `/api/v1/broadcast-campaigns/${route.params.id}/recipients/${recipient.id}/open`,
    ).post()

    const body = response?.data ?? response
    const updated = body?.data ?? body

    const idx = campaign.value.recipients.findIndex(r => r.id === recipient.id)
    if (idx !== -1) campaign.value.recipients[idx] = updated

    if (openedCount.value === totalCount.value) campaign.value.status = 'completed'
  } catch (err) {
    console.error('[BroadcastCampaignQueue] gagal menandai dibuka', err)
  } finally {
    openingId.value = null
  }
}
</script>

<template>
  <VRow v-if="loading">
    <VCol
      cols="12"
      class="text-center pa-10"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </VCol>
  </VRow>

  <VRow v-else-if="loadError">
    <VCol cols="12">
      <VAlert type="error">
        {{ loadError }}
      </VAlert>
    </VCol>
  </VRow>

  <VRow v-else>
    <VCol cols="12">
      <VCard class="mb-4">
        <VCardText>
          <div class="d-flex flex-wrap align-center justify-space-between gap-4">
            <div>
              <div class="text-h6">
                {{ campaign.template_name ?? 'Campaign Broadcast' }}
              </div>
              <VChip
                :color="statusMeta(campaign.status).color"
                size="small"
                class="mt-1"
              >
                {{ statusMeta(campaign.status).label }}
              </VChip>
            </div>
            <div style="min-width: 260px">
              <div class="text-body-2 mb-1">
                {{ openedCount }} dari {{ totalCount }} sudah dibuka
              </div>
              <VProgressLinear
                :model-value="progressPercent"
                height="10"
                rounded
                color="success"
              />
            </div>
          </div>
        </VCardText>
      </VCard>

      <VAlert
        type="warning"
        variant="tonal"
        class="mb-4"
      >
        Status "dibuka" hanya menandakan tombol sudah diklik dan WhatsApp terbuka dengan pesan
        terisi. Sistem <strong>tidak bisa</strong> memastikan pesan benar-benar terkirim — itu
        tergantung Anda menekan kirim di WhatsApp masing-masing.
      </VAlert>

      <VCard>
        <VCardTitle class="text-body-1">
          Antrian Penerima
        </VCardTitle>
        <VDivider />
        <VList>
          <VListItem
            v-for="r in campaign.recipients"
            :key="r.id"
          >
            <template #prepend>
              <VAvatar
                color="primary"
                variant="tonal"
              >
                {{ r.customer_name?.charAt(0) ?? '?' }}
              </VAvatar>
            </template>

            <VListItemTitle>{{ r.customer_name }}</VListItemTitle>
            <VListItemSubtitle>{{ r.phone }} ({{ r.whatsapp_number || 'nomor tidak valid' }})</VListItemSubtitle>

            <template #append>
              <div class="d-flex align-center gap-3">
                <VChip
                  :color="r.status === 'opened' ? 'success' : 'default'"
                  size="small"
                >
                  {{ r.status === 'opened' ? 'Dibuka' : 'Belum Dibuka' }}
                </VChip>

                <VBtn
                  v-if="can('send', 'Broadcast')"
                  size="small"
                  :color="r.status === 'opened' ? 'default' : 'primary'"
                  :variant="r.status === 'opened' ? 'tonal' : 'elevated'"
                  :loading="openingId === r.id"
                  :disabled="!r.wa_link"
                  prepend-icon="ri-whatsapp-line"
                  @click="sendTo(r)"
                >
                  {{ r.status === 'opened' ? `Buka Lagi ke ${r.customer_name}` : `Kirim ke ${r.customer_name}` }}
                </VBtn>
              </div>
            </template>
          </VListItem>
        </VList>
      </VCard>
    </VCol>
  </VRow>
</template>